import * as React from 'react';

/**
 * Etat React conserve dans `sessionStorage`, pour survivre a un rechargement.
 *
 * POURQUOI c'est necessaire : le tunnel de reservation compte sept etapes. Tant que son
 * brouillon vivait dans un `useState`, un rechargement de page — une touche F5, un
 * retour arriere, un telephone qui met l'onglet en veille et le recharge — effacait
 * l'adresse, le cycle, le forfait et le creneau choisis. L'utilisateur recommencait
 * tout. C'est le genre de defaut qu'on ne voit pas en developpement, parce qu'on ne
 * recharge jamais au milieu d'un formulaire.
 *
 * POURQUOI `sessionStorage` et non `localStorage` : une reservation en cours appartient
 * a l'onglet et au moment present. Retrouver trois jours plus tard un brouillon pointant
 * un creneau depuis longtemps reserve serait plus deroutant qu'utile. `sessionStorage`
 * disparait a la fermeture de l'onglet, ce qui correspond exactement a la duree de vie
 * d'un tunnel.
 *
 * POURQUOI une peremption en plus : meme dans un onglet reste ouvert, un brouillon
 * vieux de plusieurs heures pointe des creneaux qui ne sont plus libres. On le laisse
 * donc expirer. L'API revalide de toute facon integralement les creneaux : la peremption
 * evite un refus a la derniere etape, elle ne remplace pas le controle serveur.
 *
 * CE QUI N'Y EST JAMAIS ECRIT : aucun jeton de session. Ceux-ci vivent dans un cookie
 * `httpOnly`, inaccessible a JavaScript — c'est ce qui neutralise leur vol par injection
 * de script. Y deposer une copie annulerait cette protection.
 */

interface Enveloppe<T> {
  /** Incremente a chaque changement de forme des donnees : un brouillon ecrit par une
   *  version precedente du code est ignore plutot que lu de travers. */
  version: number;
  ecritLe: number;
  valeur: T;
}

export function useEtatPersistant<T>(
  cle: string,
  valeurInitiale: T,
  options: { version?: number; peremptionMs?: number } = {},
): [T, React.Dispatch<React.SetStateAction<T>>, () => void] {
  const { version = 1, peremptionMs = 2 * 60 * 60 * 1000 } = options;

  const [valeur, setValeur] = React.useState<T>(() => {
    // Lecture unique, a l'initialisation : lire dans un effet provoquerait un premier
    // rendu avec l'etat vide, donc un clignotement du formulaire.
    try {
      const brut = sessionStorage.getItem(cle);
      if (!brut) return valeurInitiale;

      const enveloppe = JSON.parse(brut) as Enveloppe<T>;
      const perime = Date.now() - enveloppe.ecritLe > peremptionMs;
      if (enveloppe.version !== version || perime) {
        sessionStorage.removeItem(cle);
        return valeurInitiale;
      }
      return enveloppe.valeur;
    } catch {
      // Stockage indisponible (navigation privee stricte, quota atteint) ou contenu
      // illisible : on repart d'un brouillon vierge plutot que de faire echouer la page.
      return valeurInitiale;
    }
  });

  React.useEffect(() => {
    try {
      const enveloppe: Enveloppe<T> = { version, ecritLe: Date.now(), valeur };
      sessionStorage.setItem(cle, JSON.stringify(enveloppe));
    } catch {
      // Une ecriture impossible ne doit jamais interrompre la saisie en cours.
    }
  }, [cle, version, valeur]);

  const oublier = React.useCallback(() => {
    try {
      sessionStorage.removeItem(cle);
    } catch {
      /* rien a faire */
    }
  }, [cle]);

  return [valeur, setValeur, oublier];
}
